import { useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import { http } from "@network/http";
import { useDispatch, useSelector } from "noval";
import IconButton from "@mui/material/IconButton";
import { handleError } from "@network/handle-errors";
import { buildClass, checkClass } from "@utils/tag";
import useTranslation from "next-translate/useTranslation";
import dynamic from "next/dynamic";
import { Icon } from "@utils/helper";
import { useQuery } from "@tanstack/react-query";
const LinearBuffer = dynamic(() => import("@components/LinearBuffer"), {
  ssr: false,
});
const Notify = dynamic(() => import("./Notify"), {
  ssr: false,
});
const Menu = dynamic(() => import("@mui/material").then((mod) => mod.Menu), {
  ssr: false,
});
const InView = dynamic(
  () => import("react-intersection-observer").then((mod) => mod.InView),
  {
    ssr: false,
  }
);
export default function Notification() {
  const { t, lang } = useTranslation();
  const { dispatch, reset } = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const { hasMore, items } = useSelector("notifications");
  const [isQueryEnabled, setIsQueryEnabled] = useState(false);
  const screen = useSelector("screen");

  const getNotifications = () =>
    dispatch(async ({ state, update, addState }) => {
      const notifies = state?.notifications;
      if (notifies?.loading) return;
      try {
        addState({ loading: true }, "notifications");
        const page = notifies?.page || 1;
        const res = await http.get(`/all-notifications?page=${page}`);
        const data = res.data?.data;
        const oldItems = notifies?.items;
        update(
          {
            page: page + 1,
            loading: false,
            items: [...oldItems, ...data?.items],
            hasMore: data?.items?.length,
          },
          "notifications"
        );
        return state?.notifications;
      } catch (error) {
        handleError(error);
      }
    });

  const updateNotify = async (id) => {
    try {
      await http.post(`/notifications/${id}/update`).then(() => {
        dispatch(({ update }) => {
          update(
            {
              items: items.map((item) =>
                item.id === id ? { ...item, is_read: true } : item
              ),
            },
            "notifications"
          );
        });
      });
    } catch (error) {
      handleError(error);
    }
  };

  useQuery({
    queryKey: ["notifications", lang],
    queryFn: () => getNotifications(),
    enabled: isQueryEnabled,
  });

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    setIsQueryEnabled(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setTimeout(() => {
      setIsQueryEnabled(false);
      reset("notifications");
    }, 500);
  };

  return (
    <>
      <Tooltip title={t("nav.notifications")}>
        <IconButton
          onClick={handleClick}
          size="small"
          aria-controls={open ? "account-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
        >
          <div
            className={buildClass(
              "w-[30px] h-[30px] sm:w-[43px] sm:h-[43px] relative rounded-[36px]",
              "flex justify-center items-center cursor-pointer relative",
              "border border-solid border-white",
              checkClass(open, [
                "bg-[#0366FF]",
                "bg-transparent sm:bg-[#f3f5f8]",
              ])
            )}
          >
            <Icon
              id="r5-c2"
              className={
                open
                  ? "text-white"
                  : screen === "sm-phone"
                  ? "text-white"
                  : "text-[#333]"
              }
            />
          </div>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: "hidden",
            overflowY: "scroll",
            background: "#fff",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
            height: `auto`,
            mt: "6px !important",
            width: "394px",
            borderRadius: "20px",
            maxHeight: "500px",
            "& .MuiList-root.MuiMenu-list": {
              padding: "16px 8px",
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <Notify updateNotify={updateNotify} />
        {hasMore ? (
          <InView
            as="div"
            onChange={(inView) => {
              if (inView) getNotifications();
            }}
          >
            <LinearBuffer />
          </InView>
        ) : null}
      </Menu>
    </>
  );
}
